import { useEffect, useState } from "react";

import { PlusOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";

import { useTranslation } from "react-i18next";

import { useCarContext } from "../../contexts/CarContext";
import { useCategoriesContext } from "../../contexts/CategoriesContext";
import { useToast } from "../../contexts/ToastContext";
import CarListPanel from "./components/CarListPanel";
import CategoryListPanel from "./components/CategoryListPanel";
import LocationListPanel from "./components/LocationListPanel";
import useOpenModalParam from "../../hooks/useOpenModalParam";

import CarList from "./carList";
import CustomSegmentedTabs, { useSegmentedTabState } from "../../components/custom-component/CustomSegmentedTabs";
import PageContentShell from "../../components/layout/PageContentShell";
import Rates from "./rates";
import Locations from "./Locations";

import GenerateQR from "./components/generateQR/GenerateQR";
import AddCarModal from "./components/modals/AddCarModal";
import deleteFile from "../../utilities/deleteFile";

const TAB_KEYS = {
    CARS: "cars",
    CATEGORIES: "categories",
    LOCATIONS: "locations",
};

function Cars() {
    const { t } = useTranslation("cars");
    const {
        cars,
        searchParams,
        loading,
        initialLoad,
        activePageNumber,
        totalItemCount,
        pageSize,
        fetchCars,
        deleteCar,
        updateSearchParams,
        updateActivePage,
        setPageSize,
        setCars,
    } = useCarContext();

    const [addCarModal, setAddCarModal] = useState(false);
    const { activeTab, setActiveTab, handleTabChange } = useSegmentedTabState({
        defaultValue: TAB_KEYS.CARS,
        urlParam: "tab",
        validValues: [TAB_KEYS.CARS, TAB_KEYS.CATEGORIES, TAB_KEYS.LOCATIONS],
    });
    const { fetchCategories, fetchLocations, categoryLoading, verifyCarCreationPrerequisites } =
        useCategoriesContext();
    const toast = useToast();
    const [noCategoriesModal, setNoCategoriesModal] = useState(false);
    const [noLocationsModal, setNoLocationsModal] = useState(false);
    const [prerequisitesLoaded, setPrerequisitesLoaded] = useState(false);
    const [checkingAddCarPrerequisites, setCheckingAddCarPrerequisites] = useState(false);

    useEffect(() => {
        if (initialLoad) {
            fetchCars();
        }
    }, [fetchCars, initialLoad]);

    const handleConfirmDelete = async (car) => {
        toast.api.destroy();

        const result = await deleteCar(car?.id);

        if (Object.keys(car?.images || {}).length > 0) {
            const imageValues = Object.values(car.images);
            await Promise.all(
                imageValues.map((image) => {
                    if (image) {
                        return deleteFile(image);
                    }
                    return Promise.resolve();
                }),
            );
        }

        if (result.success) {
            toast.success({
                message: t("success"),
                description: t("carDeleted"),
            });
        } else {
            toast.error({
                message: t("error"),
                description: t("failedToDelete"),
            });
        }
    };

    const handleDeleteCar = async (car) => {
        Modal.confirm({
            title: t("confirmDelete"),
            content: (
                <div>
                    <p>{car?.carNumber ? t("carPlateText", { plateNumber: car?.carNumber }) : ""}</p>
                </div>
            ),
            okText: t("yes"),
            cancelText: t("no"),
            onOk: () => handleConfirmDelete(car),
            onCancel: () => toast.api.destroy(),
            centered: true,
            okButtonProps: {
                danger: true,
            },
        });
    };

    useEffect(() => {
        Promise.all([fetchCategories(), fetchLocations()]).finally(() => setPrerequisitesLoaded(true));
    }, [fetchCategories, fetchLocations]);

    const handleAddCarClick = async () => {
        setCheckingAddCarPrerequisites(true);
        try {
            const { hasCategories, hasLocations } = await verifyCarCreationPrerequisites();

            if (!hasCategories) {
                setNoCategoriesModal(true);
                return;
            }

            if (!hasLocations) {
                setNoLocationsModal(true);
                return;
            }

            setAddCarModal(true);
        } finally {
            setCheckingAddCarPrerequisites(false);
        }
    };

    useOpenModalParam("addCar", handleAddCarClick, prerequisitesLoaded);

    const handlePageSizeChange = (size) => {
        setPageSize(size);
        updateActivePage(1);
    };

    const renderDesktopCarsContent = () => (
        <CarList
            datas={cars}
            loading={loading}
            handleDeleteCar={handleDeleteCar}
            searchParams={searchParams}
            onSearchParamsChange={updateSearchParams}
            t={t}
            title={t("cars")}
            count={totalItemCount}
            onCarUpdated={(updated) =>
                setCars((prev) => prev.map((c) => (c.id === updated.id ? { ...c, isFeatured: updated.isFeatured } : c)))
            }
            pagination={{
                total: totalItemCount,
                current: activePageNumber,
                pageSize,
                onChange: updateActivePage,
                onPageSizeChange: handlePageSizeChange,
            }}
            headerExtra={
                <div className="flex items-center gap-2" data-tour="cars-add">
                    <Button
                        disabled={categoryLoading || checkingAddCarPrerequisites}
                        loading={checkingAddCarPrerequisites}
                        className="addbtn"
                        icon={<PlusOutlined />}
                        onClick={handleAddCarClick}
                        onKeyUp={(e) => {
                            if (e.key === "Enter") handleAddCarClick();
                        }}
                        aria-label={t("addCarBtn")}>
                        {t("addCarBtn")}
                    </Button>
                    <GenerateQR />
                </div>
            }
        />
    );

    const tabItems = [
        { label: t("cars"), value: TAB_KEYS.CARS },
        { label: t("category"), value: TAB_KEYS.CATEGORIES },
        { label: t("location"), value: TAB_KEYS.LOCATIONS },
    ];

    const renderActiveTabContent = () => {
        switch (activeTab) {
            case TAB_KEYS.CATEGORIES:
                return (
                    <>
                        <div className="lg:hidden">
                            <CategoryListPanel />
                        </div>
                        <div className="hidden lg:block">
                            <Rates embedded />
                        </div>
                    </>
                );
            case TAB_KEYS.LOCATIONS:
                return (
                    <>
                        <div className="lg:hidden">
                            <LocationListPanel />
                        </div>
                        <div className="hidden lg:block">
                            <Locations embedded />
                        </div>
                    </>
                );
            case TAB_KEYS.CARS:
            default:
                return (
                    <>
                        <div className="lg:hidden">
                            <CarListPanel
                                onAddCarClick={handleAddCarClick}
                                onDeleteCar={handleDeleteCar}
                                addCarLoading={checkingAddCarPrerequisites}
                            />
                        </div>
                        <div className="hidden lg:block">{renderDesktopCarsContent()}</div>
                    </>
                );
        }
    };

    return (
        <PageContentShell>
            <div className="flex min-w-0 flex-col gap-4 lg:gap-0">
                <div className="pb-0 lg:pb-4">
                    <div data-tour="cars-tabs">
                        <CustomSegmentedTabs
                            items={tabItems}
                            value={activeTab}
                            onChange={handleTabChange}
                            segmentedClassName="bg-[#F2F2FA] mb-0 w-full lg:bg-white"
                        />
                    </div>
                </div>
                {renderActiveTabContent()}
            </div>

            <AddCarModal
                open={addCarModal}
                onClose={() => setAddCarModal(false)}
                refetchCars={fetchCars}
                entryFlow="vin-first"
            />

            <Modal
                title={<h2 className="text-2xl font-bold">{t("noCategoriesTitle")}</h2>}
                open={noCategoriesModal}
                onCancel={() => setNoCategoriesModal(false)}
                footer={[
                    <button
                        key="cancel"
                        type="button"
                        className="mr-2 rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                        onClick={() => setNoCategoriesModal(false)}>
                        {t("cancel")}
                    </button>,
                    <button
                        key="create"
                        type="button"
                        className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                        onClick={() => {
                            setNoCategoriesModal(false);
                            setActiveTab(TAB_KEYS.CATEGORIES);
                        }}>
                        {t("createCategory")}
                    </button>,
                ]}
                centered
                width={400}>
                <p>{t("noCategoriesMessage")}</p>
            </Modal>

            <Modal
                title={<h2 className="text-2xl font-bold">{t("noLocationsTitle")}</h2>}
                open={noLocationsModal}
                onCancel={() => setNoLocationsModal(false)}
                footer={[
                    <button
                        key="cancel"
                        type="button"
                        className="mr-2 rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                        onClick={() => setNoLocationsModal(false)}>
                        {t("cancel")}
                    </button>,
                    <button
                        key="create"
                        type="button"
                        className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                        onClick={() => {
                            setNoLocationsModal(false);
                            setActiveTab(TAB_KEYS.LOCATIONS);
                        }}>
                        {t("createLocation")}
                    </button>,
                ]}
                centered
                width={400}>
                <p>{t("noLocationsMessage")}</p>
            </Modal>
        </PageContentShell>
    );
}

export default Cars;
